export const GENERATION_STATUS = {
  GENERATING: "generating",
  FAILED: "failed",
};

export const MAX_CONCURRENT_GENERATIONS = 5;

const MAX_GENERATION_ERROR_LENGTH = 180;
const CAROUSEL_ITEM_ID_PREFIX = "carousel-";

export const truncateGenerationErrorMessage = (errorMessage) => {
  if (typeof errorMessage !== "string") return "";
  const trimmed = errorMessage.trim();
  if (trimmed.length <= MAX_GENERATION_ERROR_LENGTH) return trimmed;
  return `${trimmed.slice(0, MAX_GENERATION_ERROR_LENGTH - 3)}...`;
};

export const getGenerationSlotsCount = ({ mediaType, numberOfImages } = {}) => {
  if (mediaType === "video" || mediaType === "carousel") {
    return 1;
  }

  let count = parseInt(numberOfImages, 10);
  if (Number.isNaN(count)) {
    count = 3;
  }
  if (count < 1) {
    count = 1;
  }
  if (count > 10) {
    count = 10;
  }

  return count;
};

const getPlaceholderIdentity = ({ generationID, slotIndex }) =>
  `generation-${generationID}-${slotIndex}`;

export const buildGenerationPlaceholders = ({
  generationID,
  mediaType,
  aspectRatio,
  numberOfImages,
  canRetry = true,
  canDismiss = false,
} = {}) => {
  const slots = getGenerationSlotsCount({ mediaType, numberOfImages });
  let resolvedMediaType = "image";
  switch (mediaType) {
    case "image":
    case "video":
    case "carousel":
      resolvedMediaType = mediaType;
      break;
    default: {
      const exhaustiveCheck = mediaType;
      void exhaustiveCheck;
      resolvedMediaType = "image";
    }
  }

  return Array.from({ length: slots }, (_, slotIndex) => {
    const identity = getPlaceholderIdentity({ generationID, slotIndex });
    return {
      id: identity,
      stableKey: identity,
      url: identity,
      generationID,
      status: GENERATION_STATUS.GENERATING,
      mediaType: resolvedMediaType,
      aspectRatio: aspectRatio || "1:1",
      slotIndex,
      canRetry,
      canDismiss,
    };
  });
};

export const isGenerationPlaceholder = (item) => {
  if (!item || typeof item !== "object") return false;
  if (!item.generationID) return false;

  switch (item.status) {
    case GENERATION_STATUS.GENERATING:
    case GENERATION_STATUS.FAILED:
      return true;
    default: {
      const exhaustiveCheck = item.status;
      void exhaustiveCheck;
      return false;
    }
  }
};

export const stripGenerationPlaceholders = (items) => {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => !isGenerationPlaceholder(item));
};

export const getPendingGenerationIDs = (items) => {
  const ids = new Set();
  if (!Array.isArray(items)) return ids;

  items.forEach((item) => {
    if (item?.status === GENERATION_STATUS.GENERATING && item.generationID) {
      ids.add(item.generationID);
    }
  });

  return ids;
};

const splitGenerationGroup = ({ items, generationID }) => {
  const list = Array.isArray(items) ? items : [];
  const start = list.findIndex((item) => item?.generationID === generationID);
  if (start < 0) {
    return { found: false, before: list, after: [] };
  }

  const before = list.slice(0, start);
  const after = list
    .slice(start)
    .filter((item) => item?.generationID !== generationID);

  return { found: true, before, after };
};

export const replaceGenerationPlaceholders = ({
  items,
  generationID,
  media,
} = {}) => {
  const nextMedia = Array.isArray(media) ? media : [];
  const { found, before, after } = splitGenerationGroup({
    items,
    generationID,
  });

  if (!found) {
    return [...nextMedia, ...(Array.isArray(items) ? items : [])];
  }

  return [...before, ...nextMedia, ...after];
};

export const markGenerationFailed = ({
  items,
  generationID,
  errorMessage,
  canRetry,
} = {}) => {
  const { found, before, after } = splitGenerationGroup({
    items,
    generationID,
  });
  if (!found) return Array.isArray(items) ? items : [];

  const template = (Array.isArray(items) ? items : []).find(
    (item) => item?.generationID === generationID
  );
  const identity = getPlaceholderIdentity({ generationID, slotIndex: 0 });

  const failedItem = {
    id: identity,
    stableKey: identity,
    url: identity,
    generationID,
    status: GENERATION_STATUS.FAILED,
    mediaType: template?.mediaType || "image",
    aspectRatio: template?.aspectRatio || "1:1",
    slotIndex: 0,
    errorMessage: truncateGenerationErrorMessage(errorMessage),
    canRetry: canRetry ?? template?.canRetry !== false,
    canDismiss: template?.canDismiss === true,
  };

  return [...before, failedItem, ...after];
};

export const removeGeneration = ({ items, generationID } = {}) => {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => item?.generationID !== generationID);
};

export const buildExternalGenerationID = ({ jobID, alertId, id } = {}) => {
  const source = jobID || alertId || id || "EXTERNAL";
  return `external-${source}`;
};

export const buildInitialGeneratedMedia = ({
  generatedMedia = [],
  isGenerating = false,
  generationID,
  mediaType,
  aspectRatio,
  numberOfImages,
} = {}) => {
  const realMedia = stripGenerationPlaceholders(generatedMedia);
  if (isGenerating !== true || !generationID) return realMedia;

  return [
    ...buildGenerationPlaceholders({
      generationID,
      mediaType,
      aspectRatio,
      numberOfImages,
      canDismiss: true,
    }),
    ...realMedia,
  ];
};

export const mergeExternalGenerationMedia = ({
  items = [],
  incomingMedia = [],
  externalPlaceholders = [],
} = {}) => {
  const externalID = externalPlaceholders[0]?.generationID;
  const localPending = (Array.isArray(items) ? items : []).filter(
    (item) => isGenerationPlaceholder(item) && item.generationID !== externalID
  );

  return [
    ...localPending,
    ...externalPlaceholders,
    ...stripGenerationPlaceholders(incomingMedia),
  ];
};

export const collectMediaIdentityKeys = (items = []) => {
  const keys = [];
  if (!Array.isArray(items)) return keys;

  items.forEach((item) => {
    if (isGenerationPlaceholder(item) || item?.resizingFormat) return;

    if (typeof item === "string") {
      keys.push(item);
      return;
    }

    if (item?.type === "carousel" && Array.isArray(item.urls)) {
      keys.push(...item.urls.filter(Boolean));
      if (item.carouselID) keys.push(`carousel:${item.carouselID}`);
      return;
    }

    if (item?.url) keys.push(item.url);
    if (item?.carouselID) keys.push(`carousel:${item.carouselID}`);
  });

  return keys;
};

export const getUnseenMedia = ({ candidates = [], existing = [] } = {}) => {
  const seen = new Set(collectMediaIdentityKeys(existing));
  if (!Array.isArray(candidates)) return [];

  return candidates.filter((item) => {
    if (isGenerationPlaceholder(item)) return false;
    const keys = collectMediaIdentityKeys([item]);
    if (keys.length === 0) return false;
    return keys.some((key) => !seen.has(key));
  });
};

const getCarouselIDFromItemId = (itemId) => {
  if (
    typeof itemId !== "string" ||
    !itemId.startsWith(CAROUSEL_ITEM_ID_PREFIX)
  ) {
    return "";
  }
  return itemId.slice(CAROUSEL_ITEM_ID_PREFIX.length);
};

export const findGeneratedMediaIndex = ({
  items = [],
  editData = {},
  fallbackIndex,
} = {}) => {
  const list = Array.isArray(items) ? items : [];
  const sourceUrl = typeof editData?.url === "string" ? editData.url : "";
  const carouselID =
    editData?.carouselID || getCarouselIDFromItemId(editData?.itemId);

  if (carouselID) {
    const byCarousel = list.findIndex(
      (item) =>
        item?.type === "carousel" &&
        Array.isArray(item.urls) &&
        item.carouselID === carouselID
    );
    if (byCarousel >= 0) return byCarousel;
  }

  if (sourceUrl) {
    const byUrl = list.findIndex((item) => {
      if (isGenerationPlaceholder(item) || item?.resizingFormat) return false;
      if (typeof item === "string") return item === sourceUrl;
      if (item?.url === sourceUrl) return true;
      if (Array.isArray(item?.urls) && item.urls.includes(sourceUrl)) {
        return true;
      }
      return false;
    });
    if (byUrl >= 0) return byUrl;
  }

  if (
    Number.isInteger(fallbackIndex) &&
    fallbackIndex >= 0 &&
    fallbackIndex < list.length &&
    !isGenerationPlaceholder(list[fallbackIndex]) &&
    !list[fallbackIndex]?.resizingFormat
  ) {
    return fallbackIndex;
  }

  return -1;
};

export const mapGenerateResponseToMedia = ({ response, mediaType } = {}) => {
  const responseMedia = response?.data?.images || response?.data?.videos || [];

  if (mediaType === "video") {
    return { media: responseMedia, newRatings: {} };
  }

  const newRatings = {};
  const responseCarouselID = response?.data?.carouselID;

  if (responseCarouselID) {
    const carouselUrls = responseMedia
      .map((image) => {
        if (image?.url) newRatings[image.url] = image.rating;
        return image?.url;
      })
      .filter(Boolean);

    return {
      media: [
        {
          type: "carousel",
          carouselID: responseCarouselID,
          urls: carouselUrls,
          rating: null,
        },
      ],
      newRatings,
    };
  }

  const media = responseMedia
    .map((image) => {
      if (!image?.url) return null;
      newRatings[image.url] = image.rating;
      if (image.carouselID) {
        return {
          url: image.url,
          carouselID: image.carouselID,
          rating: image.rating,
        };
      }
      return image.url;
    })
    .filter(Boolean);

  return { media, newRatings };
};

export const getPendingSpendForUsageKey = ({
  reservations = [],
  usageKey,
} = {}) => {
  if (!Array.isArray(reservations) || !usageKey) return 0;
  return reservations.reduce((sum, reservation) => {
    if (reservation?.usageKey !== usageKey) return sum;
    const amount = Number(reservation.amount) || 0;
    return sum + amount;
  }, 0);
};

export const addPendingReservation = ({
  reservations = [],
  reservationID,
  usageKey,
  amount,
} = {}) => {
  const list = Array.isArray(reservations) ? reservations : [];
  if (!reservationID || !usageKey || !(Number(amount) > 0)) return list;
  return [...list, { reservationID, usageKey, amount: Number(amount) }];
};

export const releasePendingReservation = ({
  reservations = [],
  reservationID,
} = {}) => {
  if (!Array.isArray(reservations)) return [];
  return reservations.filter(
    (reservation) => reservation?.reservationID !== reservationID
  );
};

// Allowlist of the fields `startGenerateImages` / `startGenerateVideo` accept.
// A notification snapshot may carry extra keys (jobID, alertId, ...) that must
// never be re-sent on retry.
const RETRY_PARAM_KEYS = [
  "prompt",
  "language",
  "accent",
  "hasWatermark",
  "location",
  "size",
  "websiteUrl",
  "imagesUrl",
  "mediaType",
  "hasText",
  "hasSubtitles",
  "subtitleOptions",
  "logoUrl",
  "hasLogo",
  "campaignID",
  "campaignContext",
  "aspectRatio",
  "videoType",
  "duration",
  "generatedMedia",
  "ratings",
  "platform",
  "adTemplate",
  "isCarousel",
  "numberOfImages",
  "referenceAdUrl",
  "referenceAdMediaType",
  "additionalAssets",
];

export const buildRetryParamsFromInitialValues = (initialValues = {}) => {
  if (!initialValues || typeof initialValues !== "object") return null;

  const isVideo = initialValues.mediaType === "video";
  const isCarousel =
    initialValues.isCarousel === true || initialValues.mediaType === "carousel";
  const mediaType = isVideo ? "video" : isCarousel ? "carousel" : "image";

  let numberOfImages = parseInt(initialValues.numberOfImages, 10);
  if (Number.isNaN(numberOfImages)) {
    numberOfImages = isVideo ? 1 : 3;
  }
  if (numberOfImages < 1) numberOfImages = 1;
  if (numberOfImages > 10) numberOfImages = 10;

  const generateParams = {};
  RETRY_PARAM_KEYS.forEach((key) => {
    if (initialValues[key] !== undefined) {
      generateParams[key] = initialValues[key];
    }
  });
  if (Array.isArray(generateParams.generatedMedia)) {
    generateParams.generatedMedia = stripGenerationPlaceholders(
      generateParams.generatedMedia
    );
  }

  return {
    generateParams,
    mediaType,
    aspectRatio: initialValues.aspectRatio || (isVideo ? "9:16" : "1:1"),
    numberOfImages,
    multiplier: isVideo ? 1 : numberOfImages,
    isVideo,
    canRetry: true,
  };
};

const readGenerationMessage = (source = {}, message = "") => {
  if (typeof message === "string" && message) return message;
  if (
    typeof source.jobResponse?.message === "string" &&
    source.jobResponse.message
  ) {
    return source.jobResponse.message;
  }
  if (typeof source.errorMessage === "string") return source.errorMessage;
  return "";
};

export const buildCreateWithIAInitialValues = ({
  source = {},
  jobID,
  alertId,
  message = "",
  alertIsTracked = true,
} = {}) => {
  const resolvedJobID = jobID || source.jobID;
  const errorMessage = readGenerationMessage(source, message);
  const alertClosedWhileGenerating =
    alertIsTracked !== true && source.isGenerating === true;

  return {
    ...source,
    ...(resolvedJobID ? { jobID: resolvedJobID } : {}),
    ...(alertId ? { alertId } : {}),
    ...(errorMessage ? { errorMessage } : {}),
    ...(alertClosedWhileGenerating
      ? { isGenerating: false, alertClosed: true }
      : {}),
  };
};

/**
 * Applies one external notification sync without resurrecting a dismissed group
 * or replacing local gallery items that are already on screen.
 */
export const applyExternalGenerationSync = ({
  items = [],
  generationID,
  isGenerating = false,
  incomingMedia = [],
  placeholders = [],
  dismissed = false,
} = {}) => {
  const list = Array.isArray(items) ? items : [];
  if (!generationID || dismissed) {
    return { items: list, didInsertResults: false };
  }

  if (!isGenerating) {
    const fresh = getUnseenMedia({
      candidates: stripGenerationPlaceholders(incomingMedia),
      existing: stripGenerationPlaceholders(list),
    });
    if (fresh.length > 0) {
      return {
        items: replaceGenerationPlaceholders({
          items: list,
          generationID,
          media: fresh,
        }),
        didInsertResults: true,
      };
    }

    const hasGroup = list.some((item) => item?.generationID === generationID);
    if (!hasGroup) {
      return { items: list, didInsertResults: false };
    }

    // A failed job drops its placeholders; no failed card is shown.
    return {
      items: removeGeneration({ items: list, generationID }),
      didInsertResults: false,
    };
  }

  const hasGroup = list.some((item) => item?.generationID === generationID);
  if (hasGroup) {
    return { items: list, didInsertResults: false };
  }

  return {
    items: mergeExternalGenerationMedia({
      items: list,
      incomingMedia,
      externalPlaceholders: placeholders,
    }),
    didInsertResults: false,
  };
};
